import { Button } from "@/components/ui/button";
import {
  Building2, TrendingUp, Shield, Target, ChevronRight,
  Home, Banknote, BarChart3, Trophy,
  Key, DollarSign, ShieldCheck, User, Wrench, BarChart2, Landmark,
  Award, Scale, GraduationCap, Gem, Star
} from "lucide-react";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface Props {
  onStart: () => void;
}

const INTRO_POPUPS = [
  {
    icon: Building2,
    title: "What is real estate investing?",
    body: "Buying property to earn money through rent, value growth, or both.",
  },
  {
    icon: TrendingUp,
    title: "How investors make money",
    body: "Through monthly cash flow, property appreciation, or strategic resale.",
  },
  {
    icon: Shield,
    title: "Why this matters",
    body: "Property builds long-term wealth — but every investment has tradeoffs.",
  },
  {
    icon: Target,
    title: "Your mission",
    body: "Analyze deals, purchase properties, manage tenants, and build a profitable portfolio.",
  },
];

const FEATURE_CARDS = [
  { icon: Home, label: "Buy Properties" },
  { icon: Banknote, label: "Earn Cash Flow" },
  { icon: BarChart3, label: "Build Equity" },
  { icon: Trophy, label: "Earn Achievements" },
];

const ACHIEVEMENT_ICONS = [
  Key, DollarSign, ShieldCheck, User, Wrench,
  BarChart2, Landmark, Award, Scale, GraduationCap, Gem, Star,
];

export default function IntroScreen({ onStart }: Props) {
  const [popupIdx, setPopupIdx] = useState(-1);
  const [starting, setStarting] = useState(false);

  const handleStart = () => {
    setStarting(true);
    setPopupIdx(0);
  };

  if (popupIdx >= 0 && popupIdx < INTRO_POPUPS.length) {
    const popup = INTRO_POPUPS[popupIdx];
    const Icon = popup.icon;
    return (
      <AnimatePresence mode="wait">
        <motion.div
          key={popupIdx}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-background/85 backdrop-blur-md z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="max-w-md w-full rounded-2xl border border-border/60 bg-card p-8 space-y-6 shadow-2xl shadow-primary/5"
          >
            <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto">
              <Icon className="w-8 h-8 text-primary" />
            </div>
            <h3 className="font-heading text-lg font-bold text-foreground text-center">{popup.title}</h3>
            <p className="text-sm text-muted-foreground text-center leading-relaxed">{popup.body}</p>
            <div className="flex items-center justify-between pt-2">
              <div className="flex gap-1.5">
                {INTRO_POPUPS.map((_, i) => (
                  <div
                    key={i}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      i === popupIdx ? "w-6 bg-primary" : i < popupIdx ? "w-3 bg-primary/40" : "w-3 bg-muted"
                    }`}
                  />
                ))}
              </div>
              <Button
                onClick={() => {
                  if (popupIdx < INTRO_POPUPS.length - 1) setPopupIdx(popupIdx + 1);
                  else onStart();
                }}
                className="gap-2"
              >
                {popupIdx < INTRO_POPUPS.length - 1 ? "Next" : "Let's Begin"}
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-[80vh]">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-lg w-full text-center space-y-8"
      >
        {/* Header */}
        <div className="space-y-3">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-primary" />
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gradient-blue">
            Real Estate Investing
          </h1>
          <p className="text-sm text-muted-foreground max-w-xs mx-auto leading-relaxed">
            Analyze deals, manage risks, and grow your property portfolio.
          </p>
        </div>

        {/* What You'll Learn */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-2.5 text-left"
        >
          <p className="text-xs font-heading font-semibold text-primary uppercase tracking-wider">What You'll Learn</p>
          {[
            "Analyze property deals using cap rate and cash-on-cash return",
            "Balance cash flow vs. appreciation strategies",
            "Manage tenants, vacancies, and maintenance reserves",
            "Respond to market events and economic shifts",
            "Build a diversified real estate portfolio over time",
          ].map((obj, i) => (
            <div key={i} className="flex items-start gap-2">
              <Key className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
              <span className="text-xs text-foreground/80">{obj}</span>
            </div>
          ))}
        </motion.div>

        {/* Feature cards */}
        <div className="grid grid-cols-2 gap-3">
          {FEATURE_CARDS.map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.08 }}
                className="rounded-2xl border border-border/60 bg-card/80 p-5 flex flex-col items-center gap-3 hover:border-primary/30 transition-colors duration-200"
              >
                <div className="w-11 h-11 rounded-xl bg-primary/8 border border-primary/15 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <p className="text-xs font-heading font-semibold text-foreground tracking-wide">{item.label}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Achievements */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="rounded-2xl border border-border/60 bg-card/60 p-5"
        >
          <p className="text-xs font-heading text-muted-foreground mb-4 uppercase tracking-widest">Achievements to Unlock</p>
          <div className="flex flex-wrap justify-center gap-2.5">
            {ACHIEVEMENT_ICONS.map((AIcon, i) => (
              <div
                key={i}
                className="w-10 h-10 rounded-xl bg-muted/40 border border-border/40 flex items-center justify-center hover:bg-primary/10 hover:border-primary/30 transition-all duration-200 cursor-default"
              >
                <AIcon className="w-4.5 h-4.5 text-muted-foreground/60" />
              </div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <Button
          onClick={handleStart}
          disabled={starting}
          size="lg"
          className="w-full gap-2 text-base h-13 rounded-xl"
        >
          {starting ? (
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
              Loading…
            </span>
          ) : (
            <>
              Start Module
              <ChevronRight className="w-5 h-5" />
            </>
          )}
        </Button>
      </motion.div>
    </div>
  );
}
